import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { CheckCircle2, XCircle } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";

const FILTERS = ["", "open", "resolved", "rejected"];
const badge = (s, t) => {
  const map = { open: "bg-yellow-100 text-yellow-700", resolved: "bg-emeraude/15 text-emeraude-dark", rejected: "bg-red-100 text-red-600" };
  return <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${map[s] || "bg-sand text-muted2"}`}>{t(`litiges.${s || "open"}`)}</span>;
};

export function AdminDisputesPanel() {
  const { t } = useTranslation();
  const [filter, setFilter] = useState("");
  const [rows, setRows] = useState([]);

  const load = useCallback(() => {
    api.get("/admin/disputes", { params: filter ? { status: filter } : {} }).then((r) => setRows(r.data.disputes)).catch(() => {});
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try { await api.put(`/admin/disputes/${id}/status`, { status }); toast.success(t(`litiges.${status}`)); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-disputes-panel">
      <div className="flex gap-2 mb-4 flex-wrap">
        {FILTERS.map((f) => (
          <button key={f || "all"} data-testid={`dispute-filter-${f || "all"}`} onClick={() => setFilter(f)}
            className={`px-3 py-1 rounded-full text-xs font-bold transition-colors ${filter === f ? "bg-canard text-white" : "bg-white border border-border text-ink"}`}>
            {f ? t(`litiges.${f}`) : t("litiges.all") }
          </button>
        ))}
      </div>
      {!rows.length ? (
        <div className="text-center py-12 bg-white rounded-2xl border border-border text-muted2 font-body">{t("litiges.empty")}</div>
      ) : (
        <div className="bg-white rounded-2xl shadow-card border border-border overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-sand"><tr className="text-left text-muted2 font-bold text-xs uppercase"><th className="p-3">{t("litiges.ref")}</th><th className="p-3">{t("litiges.loueur")}</th><th className="p-3">{t("litiges.client")}</th><th className="p-3">{t("litiges.amount")}</th><th className="p-3">{t("litiges.reason")}</th><th className="p-3">{t("litiges.status")}</th><th className="p-3">{t("dash.action")}</th></tr></thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.id} data-testid={`admin-dispute-row-${d.id}`} className="border-t border-border font-body">
                  <td className="p-3 font-bold text-canard-dark">{d.ref}</td>
                  <td className="p-3">{d.loueur_name}</td>
                  <td className="p-3">{d.driver_name}</td>
                  <td className="p-3 font-bold">{d.amount} €</td>
                  <td className="p-3 max-w-[200px] truncate">{d.dispute?.reason}</td>
                  <td className="p-3">{badge(d.dispute?.status, t)}</td>
                  <td className="p-3">
                    <div className="flex gap-1.5">
                      <Button size="sm" variant="outline" data-testid={`resolve-dispute-${d.id}`} onClick={() => setStatus(d.id, "resolved")} className="rounded-full h-8 text-emeraude-dark border-emeraude/30"><CheckCircle2 className="w-3.5 h-3.5 mr-1" />{t("litiges.resolve")}</Button>
                      <Button size="sm" variant="outline" data-testid={`reject-dispute-${d.id}`} onClick={() => setStatus(d.id, "rejected")} className="rounded-full h-8 text-destructive border-destructive/30"><XCircle className="w-3.5 h-3.5 mr-1" />{t("litiges.reject")}</Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
